import { Breadcrumbs, Grid, Typography, Link, Box, Chip, Tooltip, Skeleton } from "@mui/material"
import { useSearchParams } from "react-router-dom";
import { IMarketplace } from "../../../../interfaces/IMarketplace";
import { ConteudoContainer, PageContainer, PainelContainer } from "../../styles";
import { NavegacaoSecoes, SecaoNavegacao } from "../../components/NavegacaoSecoes";
import { PainelSecao } from "../../components/PainelSecao";
import { LinhaDado } from "../../components/LinhaDado";
import { StatusMarketplacePaper } from "../../components/StatusMarketplacePaper";
import { QRCodeMarketplacePaper } from "../../components/QRCodeMarketplacePaper";
import { TipoMarketplaceCor, TipoMarketplaceDescricao, TipoMarketplaceTitulo } from "../../../../enum/TipoMarketplace";
import { PersonalizacaoMarketplacePaper } from "../../components/PersonalizacaoPaper";
import { MetodosPagamentoMarketplacePaper } from "../../components/MetodosPagamentoMarketplacePaper";
import { HorariosMarketplacePaper } from "../../components/HorariosMarketplacePaper";
import { EnderecoMarketplacePaper } from "../../components/EnderecoMarketplacePaper";
import { RegioesEntregaMarketplacePaper } from "../../components/RegioesEntregaMarketplacePaper";
import {
    BadgeOutlined,
    DashboardOutlined,
    EventAvailableOutlined,
    ExtensionOutlined,
    LocalShippingOutlined,
    LockOutlined,
    ReceiptLongOutlined,
    ScheduleOutlined
} from "@mui/icons-material";
import { TipoCobrancaEntregaContainer } from "../../components/TipoCobrancaEntregaContainer";
import { TipoCobrancaEntrega } from "../../../../../pedidos/enum/TipoCobrancaEntrega";
import { DistanciasEntregaMarketplacePaper } from "../../components/DistanciasEntregaMarketplacePaper";
import { PixelMetaContainer } from "../../components/PixelMetaContainer";
import { LogRocketContainer } from "../../components/LogRocketContainer";
import { TipoAutenticacaoCardapioContainer } from "../../components/TipoAutenticacaoCardapioContainer";
import { AgendamentoPedidoContainer } from "../../components/AgendamentoPedidoContainer";
import { CabecalhoMarketplace } from "../../components/CabecalhoMarketplace";
import { SecaoConfiguracao } from "../../../../../core/components/SecaoConfiguracao";
import { useMoeda } from "../../../../../core/hooks/useMoeda";

interface CardapioOnlineContainerProps {
    marketplace?: IMarketplace
    handleRefresh: () => void,
    loadingFetch: boolean
}

type SecaoId = "geral" | "funcionamento" | "entrega" | "pedidos" | "integracoes";

const secoes: SecaoNavegacao<SecaoId>[] = [
    { id: "geral", titulo: "Visão geral", icone: <DashboardOutlined /> },
    { id: "funcionamento", titulo: "Funcionamento", icone: <ScheduleOutlined /> },
    { id: "entrega", titulo: "Entrega", icone: <LocalShippingOutlined /> },
    { id: "pedidos", titulo: "Pedidos e pagamento", icone: <ReceiptLongOutlined /> },
    { id: "integracoes", titulo: "Integrações", icone: <ExtensionOutlined /> },
];

export const CardapioOnlineContainer = ({ marketplace, handleRefresh, loadingFetch }: CardapioOnlineContainerProps) => {
    const { real } = useMoeda();

    // A seção fica na URL para não ser perdida quando a página é recarregada
    // depois de salvar uma configuração (handleRefresh remonta o container).
    const [parametros, setParametros] = useSearchParams();
    const secaoParametro = parametros.get("secao") as SecaoId | null;
    const secaoAtiva: SecaoId = secoes.some(secao => secao.id === secaoParametro)
        ? secaoParametro as SecaoId
        : "geral";

    const setSecaoAtiva = (secao: SecaoId) => {
        const novosParametros = new URLSearchParams(parametros);
        novosParametros.set("secao", secao);
        setParametros(novosParametros, { replace: true });
    }

    return (
        <PageContainer>
            <Breadcrumbs aria-label="breadcrumb">
                <Link color="primary" href="/marketplace">
                    Marketplaces
                </Link>
                {marketplace && (
                    <Typography color="text.primary">{marketplace.nome}</Typography>
                )}
            </Breadcrumbs>
            {!marketplace ? (
                <Box sx={{ mt: 3 }}>
                    <Skeleton variant="rounded" height={220} sx={{ borderRadius: "16px" }} />
                    <Skeleton variant="rounded" height={320} sx={{ borderRadius: "16px", mt: 3 }} />
                </Box>
            ) : (
                <>
                    <Box sx={{ mt: 2 }}>
                        <CabecalhoMarketplace marketplace={marketplace} />
                    </Box>
                    <ConteudoContainer>
                        <NavegacaoSecoes
                            secoes={secoes}
                            secaoAtiva={secaoAtiva}
                            onChange={setSecaoAtiva}
                        />
                        <PainelContainer>
                            <PainelSecao ativo={secaoAtiva === "geral"}>
                                <Grid item xs={12} md={6}>
                                    <StatusMarketplacePaper marketplace={marketplace} />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <QRCodeMarketplacePaper marketplace={marketplace} />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <SecaoConfiguracao
                                        titulo="Dados"
                                        descricao="Informações apresentadas aos clientes"
                                        icone={<BadgeOutlined />}
                                    >
                                        <LinhaDado rotulo="Nome público">
                                            <Typography variant="body2" fontWeight={500}>
                                                {marketplace.configuracao?.nomePublico ?? "-"}
                                            </Typography>
                                        </LinhaDado>
                                        <LinhaDado rotulo="Nome interno">
                                            <Typography variant="body2" fontWeight={500}>
                                                {marketplace.nome ?? "-"}
                                            </Typography>
                                        </LinhaDado>
                                        <LinhaDado rotulo="Telefone">
                                            <Typography variant="body2" fontWeight={500}>
                                                {marketplace.configuracao?.telefone ?? "-"}
                                            </Typography>
                                        </LinhaDado>
                                        <LinhaDado rotulo="Valor mínimo">
                                            <Typography variant="body2" fontWeight={500}>
                                                {real(marketplace.configuracao?.valorMinimo)}
                                            </Typography>
                                        </LinhaDado>
                                        <LinhaDado rotulo="Tipo">
                                            <Tooltip title={TipoMarketplaceDescricao[marketplace.tipo]}>
                                                <Chip
                                                    size="small"
                                                    label={TipoMarketplaceTitulo[marketplace.tipo]}
                                                    style={{ background: TipoMarketplaceCor[marketplace.tipo], color: "white" }}
                                                />
                                            </Tooltip>
                                        </LinhaDado>
                                        <LinhaDado rotulo="Descrição">
                                            <Typography variant="body2" fontWeight={500}>
                                                {marketplace.descricao || "-"}
                                            </Typography>
                                        </LinhaDado>
                                    </SecaoConfiguracao>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <PersonalizacaoMarketplacePaper marketplace={marketplace} />
                                </Grid>
                            </PainelSecao>

                            <PainelSecao ativo={secaoAtiva === "funcionamento"}>
                                <Grid item xs={12} md={6}>
                                    <EnderecoMarketplacePaper marketplace={marketplace} />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <HorariosMarketplacePaper refresh={handleRefresh} marketplace={marketplace} />
                                </Grid>
                            </PainelSecao>

                            <PainelSecao ativo={secaoAtiva === "entrega"}>
                                <Grid item xs={12}>
                                    <SecaoConfiguracao
                                        titulo="Cobrança de entrega"
                                        descricao="Define como o valor do frete é calculado no cardápio"
                                        icone={<LocalShippingOutlined />}
                                    >
                                        <TipoCobrancaEntregaContainer refresh={handleRefresh} marketplace={marketplace} />
                                    </SecaoConfiguracao>
                                </Grid>
                                {marketplace?.configuracao?.tipoCobrancaEntrega == TipoCobrancaEntrega.REGIAO && (
                                    <Grid item xs={12}>
                                        <RegioesEntregaMarketplacePaper isLoading={loadingFetch} refresh={handleRefresh} marketplace={marketplace} />
                                    </Grid>
                                )}
                                {marketplace?.configuracao?.tipoCobrancaEntrega == TipoCobrancaEntrega.DISTANCIA && (
                                    <Grid item xs={12}>
                                        <DistanciasEntregaMarketplacePaper isLoading={loadingFetch} refresh={handleRefresh} marketplace={marketplace} />
                                    </Grid>
                                )}
                            </PainelSecao>

                            <PainelSecao ativo={secaoAtiva === "pedidos"}>
                                <Grid item xs={12}>
                                    <MetodosPagamentoMarketplacePaper refresh={handleRefresh} marketplace={marketplace} />
                                </Grid>
                                <Grid item xs={12}>
                                    <SecaoConfiguracao
                                        titulo="Tipo de autenticação"
                                        descricao="Como o cliente se identifica antes de finalizar o pedido"
                                        icone={<LockOutlined />}
                                    >
                                        <TipoAutenticacaoCardapioContainer refresh={handleRefresh} marketplace={marketplace} />
                                    </SecaoConfiguracao>
                                </Grid>
                                <Grid item xs={12}>
                                    <SecaoConfiguracao
                                        titulo="Agendamento de pedido"
                                        descricao="Permite que o cliente escolha quando quer receber o pedido"
                                        icone={<EventAvailableOutlined />}
                                    >
                                        <AgendamentoPedidoContainer refresh={handleRefresh} marketplace={marketplace} />
                                    </SecaoConfiguracao>
                                </Grid>
                            </PainelSecao>

                            <PainelSecao ativo={secaoAtiva === "integracoes"}>
                                <Grid item xs={12} md={6}>
                                    <PixelMetaContainer isLoading={loadingFetch} refresh={handleRefresh} marketplace={marketplace} />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <LogRocketContainer isLoading={loadingFetch} refresh={handleRefresh} marketplace={marketplace} />
                                </Grid>
                            </PainelSecao>
                        </PainelContainer>
                    </ConteudoContainer>
                </>
            )}
        </PageContainer>
    )
}
